<template>
  <div class="admin-manage-page">
    <header class="page-header">
      <div>
        <p class="eyebrow">DERAS DISPATCH OPS</p>
        <h1>派单运维中心</h1>
        <p>查看自动派单队列、候选机器人评分、超时流转和失败原因，必要时由管理员手动重派、开放抢单或指定机器人。</p>
      </div>
      <button class="primary-btn" @click="fetchDispatches">刷新队列</button>
    </header>

    <section class="metric-grid">
      <article class="metric-card accent">
        <span>派单记录</span>
        <strong>{{ summary.total || 0 }}</strong>
        <p>当前筛选范围</p>
      </article>
      <article class="metric-card">
        <span>等待确认</span>
        <strong>{{ summary.pending || 0 }}</strong>
        <p>候选机器人处理中</p>
      </article>
      <article class="metric-card">
        <span>已锁单</span>
        <strong>{{ summary.accepted || 0 }}</strong>
        <p>并发竞争成功</p>
      </article>
      <article class="metric-card warning">
        <span>拒绝/超时</span>
        <strong>{{ (summary.rejected || 0) + (summary.expired || 0) }}</strong>
        <p>需要继续流转</p>
      </article>
      <article class="metric-card danger">
        <span>已取消</span>
        <strong>{{ summary.cancelled || 0 }}</strong>
        <p>管理员或锁单后释放</p>
      </article>
    </section>

    <section class="metric-grid effectiveness-grid">
      <article class="metric-card">
        <span>派单成功率</span>
        <strong>{{ percent(effectiveness.successRate) }}</strong>
        <p>已接受 / 全部邀请</p>
      </article>
      <article class="metric-card">
        <span>平均接受时间</span>
        <strong>{{ effectiveness.avgAcceptSeconds || 0 }}s</strong>
        <p>候选确认效率</p>
      </article>
      <article class="metric-card warning">
        <span>超时率</span>
        <strong>{{ percent(effectiveness.timeoutRate) }}</strong>
        <p>需优化候选或超时</p>
      </article>
      <article class="metric-card warning">
        <span>拒绝率</span>
        <strong>{{ percent(effectiveness.rejectRate) }}</strong>
        <p>机器人主动拒绝</p>
      </article>
      <article class="metric-card danger">
        <span>兜底开放</span>
        <strong>{{ effectiveness.fallbackCount || 0 }}</strong>
        <p>暂无候选后进入抢单池</p>
      </article>
    </section>

    <section class="ops-alert-card">
      <div class="section-title compact">
        <div>
          <p class="eyebrow">OPS ATTENTION</p>
          <h3>运营关注</h3>
        </div>
        <span>把无人接单、开放抢单、阻断原因集中拎出来，方便管理员快速兜底。</span>
      </div>
      <div class="ops-alert-grid">
        <article class="warning">
          <span>开放抢单</span>
          <strong>{{ taskSummary.openGrabTasks || openGrabAttentionCount }}</strong>
          <p>自动派单已进入兜底池</p>
        </article>
        <article class="danger">
          <span>派单阻断</span>
          <strong>{{ taskSummary.blockedTasks || blockedAttentionCount }}</strong>
          <p>需要处理机器人或任务条件</p>
        </article>
        <article>
          <span>无人候选扫描</span>
          <strong>{{ noCandidateAttentionCount }}</strong>
          <p>连续找不到合适机器人</p>
        </article>
      </div>
      <div class="ops-attention-list">
        <button v-for="row in opsAttentionRows" :key="`ops-${row.id}`" type="button" @click="openTaskDetail(row)">
          <strong>{{ row.taskTitle || `任务 #${row.id}` }}</strong>
          <span>{{ opsAttentionReason(row) }}</span>
        </button>
        <p v-if="!opsAttentionRows.length" class="empty-note">暂无高风险派单任务。</p>
      </div>
    </section>

    <section class="config-strip">
      <div>
        <span>派单模式</span>
        <el-select v-model="settingForm.dispatchMode" size="small">
          <el-option label="单派最优" value="SINGLE"></el-option>
          <el-option label="并发三候选" value="PARALLEL"></el-option>
          <el-option label="紧急五候选" value="URGENT"></el-option>
        </el-select>
      </div>
      <div>
        <span>并发候选</span>
        <el-input-number
          v-model="settingForm.dispatchParallelLimit"
          :min="1"
          :max="5"
          controls-position="right"
          size="small"
        ></el-input-number>
      </div>
      <div>
        <span>确认超时</span>
        <el-input-number
          v-model="settingForm.dispatchInviteTimeoutSeconds"
          :min="30"
          :max="1800"
          :step="30"
          controls-position="right"
          size="small"
        ></el-input-number>
      </div>
      <div>
        <span>自动轮次</span>
        <el-input-number
          v-model="settingForm.dispatchMaxAutoRounds"
          :min="1"
          :max="10"
          controls-position="right"
          size="small"
        ></el-input-number>
      </div>
      <div>
        <span>兜底策略</span>
        <el-switch
          v-model="settingForm.dispatchAutoOpenGrabEnabled"
          :active-value="1"
          :inactive-value="0"
          active-text="自动开放抢单"
        ></el-switch>
        <button class="mini-save-btn" @click="saveDispatchSettings">保存派单规则</button>
      </div>
    </section>

    <section class="weight-panel">
      <div class="section-title compact">
        <div>
          <p class="eyebrow">DISPATCH SCORING RULE</p>
          <h3>候选评分权重</h3>
        </div>
        <span>距离、心跳、评分、信誉、经验会共同决定机器人排序</span>
      </div>
      <div class="weight-grid">
        <label v-for="item in weightFields" :key="item.key">
          <span>{{ item.label }}</span>
          <el-input-number
            v-model="settingForm[item.key]"
            :min="0"
            :max="100"
            :step="5"
            controls-position="right"
            size="small"
          ></el-input-number>
        </label>
      </div>
    </section>

    <section class="weight-panel pricing-panel">
        <div class="section-title compact">
          <div>
            <p class="eyebrow">PLATFORM SUBSIDY RULE</p>
            <h3>动态补助 / 定价规则</h3>
          </div>
          <span>当前阶段仍是平台补助展示，不接链上扣款；无人候选时 DeRAS 可按规则自动提高补助。</span>
        </div>
        <div class="weight-grid pricing-grid">
          <label v-for="item in pricingFields" :key="item.key">
            <span>{{ item.label }}</span>
            <el-input-number
              v-model="settingForm[item.key]"
              :min="item.min"
              :max="item.max"
              :step="item.step"
              controls-position="right"
              size="small"
            ></el-input-number>
          </label>
        </div>
        <button class="mini-save-btn pricing-save-btn" @click="saveDispatchSettings">保存补助规则</button>
    </section>

    <section class="filter-card">
      <el-input
        v-model.trim="taskFilters.keyword"
        clearable
        placeholder="任务ID / 标题 / 类型"
        @keyup.enter="fetchDispatchTasks"
      ></el-input>
      <el-select v-model="taskFilters.dispatchStatus" clearable placeholder="任务派单状态" @change="fetchDispatchTasks">
        <el-option label="寻找中" value="SEARCHING"></el-option>
        <el-option label="等待确认" value="PENDING"></el-option>
        <el-option label="已锁单" value="ACCEPTED"></el-option>
        <el-option label="开放抢单" value="OPEN_GRAB"></el-option>
      </el-select>
      <button class="primary-btn" @click="fetchDispatchTasks">查询任务</button>
      <button class="secondary-btn" @click="resetTaskFilters">重置任务</button>
    </section>

    <section class="table-card task-overview-card">
      <div class="section-title">
        <div>
          <p class="eyebrow">TASK DISPATCH OVERVIEW</p>
          <h3>任务派单总览</h3>
        </div>
        <span>
          全部 {{ taskSummary.totalTasks || 0 }} · 寻找 {{ taskSummary.searchingTasks || 0 }} · 等待 {{ taskSummary.pendingTasks || 0 }} · 开放抢单 {{ taskSummary.openGrabTasks || 0 }} · 阻断 {{ taskSummary.blockedTasks || 0 }}
        </span>
      </div>
      <el-table v-loading="taskLoading" :data="dispatchTasks" @row-click="openTaskDetail">
        <el-table-column label="任务" min-width="220">
          <template #default="{ row }">
            <strong>{{ row.taskTitle || `任务 #${row.id}` }}</strong>
            <small>#{{ row.id }} · {{ serviceTypeLabel(row.serviceType) }} · {{ row.expectedRobotType || '未指定类型' }}</small>
          </template>
        </el-table-column>
        <el-table-column label="派单状态" width="160">
          <template #default="{ row }">
            <el-tag :type="dispatchTag(row.dispatchStatus)">{{ dispatchStatusLabel(row.dispatchStatus) }}</el-tag>
            <small>{{ row.dispatchStatusText || '-' }}</small>
          </template>
        </el-table-column>
        <el-table-column label="候选健康度" min-width="230">
          <template #default="{ row }">
            <span>同类 {{ row.sameTypeApprovedRobots || 0 }} · 在线 {{ row.onlineRobots || 0 }} · 空闲 {{ row.idleRobots || 0 }}</span>
            <small>接单 {{ row.acceptingRobots || 0 }} · 信誉达标 {{ row.reputationOkRobots || 0 }}</small>
          </template>
        </el-table-column>
        <el-table-column label="扫描/兜底" min-width="190">
          <template #default="{ row }">
            <span>无候选 {{ row.dispatchNoCandidateCount || 0 }} 次</span>
            <small>{{ row.dispatchLastScanAt ? `最近扫描 ${row.dispatchLastScanAt}` : '等待扫描' }}</small>
          </template>
        </el-table-column>
        <el-table-column label="阻断原因" min-width="280">
          <template #default="{ row }">
            <div class="pool-reasons compact-reasons">
              <span v-for="item in row.blockers || []" :key="`${row.id}-${item}`" class="blocked">{{ item }}</span>
              <span v-if="!(row.blockers || []).length" class="passed">派单条件通过</span>
            </div>
          </template>
        </el-table-column>
        <el-table-column label="操作" width="260" fixed="right">
          <template #default="{ row }">
            <el-button size="mini" @click.stop="openTaskDetail(row)">详情</el-button>
            <el-button size="mini" type="success" @click.stop="retryTaskById(row.id)">重派</el-button>
            <el-button size="mini" @click.stop="openGrabTaskById(row.id)">开放抢单</el-button>
          </template>
        </el-table-column>
      </el-table>
    </section>

    <section class="filter-card dispatch-filter-card">
      <el-input-number
        v-model="filters.taskId"
        :min="1"
        controls-position="right"
        placeholder="任务ID"
        @keyup.enter="fetchDispatches"
      ></el-input-number>
      <el-select v-model="filters.status" clearable placeholder="派单状态" @change="fetchDispatches">
        <el-option label="等待确认" value="PENDING"></el-option>
        <el-option label="已接受" value="ACCEPTED"></el-option>
        <el-option label="已拒绝" value="REJECTED"></el-option>
        <el-option label="已超时" value="EXPIRED"></el-option>
        <el-option label="已取消" value="CANCELLED"></el-option>
      </el-select>
      <button class="primary-btn" @click="fetchDispatches">查询</button>
      <button class="secondary-btn" @click="resetFilters">重置</button>
    </section>

    <section class="table-card">
      <el-table v-loading="loading" :data="dispatches" @row-click="openDetail">
        <el-table-column prop="id" label="派单ID" width="90"></el-table-column>
        <el-table-column label="任务" min-width="220">
          <template #default="{ row }">
            <strong>{{ row.taskTitle || `任务 #${row.taskId || '-'}` }}</strong>
            <small>#{{ row.taskId || '-' }} · {{ serviceTypeLabel(row.serviceType) }} · {{ row.expectedRobotType || '未指定类型' }}</small>
          </template>
        </el-table-column>
        <el-table-column label="候选机器人" min-width="190">
          <template #default="{ row }">
            <span>{{ row.robotName || `机器人 #${row.robotId || '-'}` }}</span>
            <small>{{ row.robotCategory || '-' }} · {{ row.ownerName || row.ownerUserId || '-' }}</small>
          </template>
        </el-table-column>
        <el-table-column label="状态" width="145">
          <template #default="{ row }">
            <el-tag :type="dispatchTag(row.dispatchStatus)">{{ dispatchStatusLabel(row.dispatchStatus) }}</el-tag>
            <small>{{ statusHint(row) }}</small>
          </template>
        </el-table-column>
        <el-table-column label="评分 / 排名" width="150">
          <template #default="{ row }">
            <span>{{ formatScore(row.dispatchScore) }}</span>
            <small>第 {{ row.candidateRank || '-' }} 候选 · {{ recommendationLabel(row.recommendationLevel) }}</small>
          </template>
        </el-table-column>
        <el-table-column label="在线/接单" width="150">
          <template #default="{ row }">
            <el-tag :type="row.sdkAgentOnline ? 'success' : 'danger'">{{ row.sdkAgentOnline ? '在线' : '离线' }}</el-tag>
            <small>{{ row.robotAcceptingOrders === 1 ? '接单开启' : '暂停接单' }}</small>
          </template>
        </el-table-column>
        <el-table-column label="邀请时间" min-width="180">
          <template #default="{ row }">
            <span>{{ row.invitedAt || '-' }}</span>
            <small>到期 {{ row.expiresAt || '-' }}</small>
          </template>
        </el-table-column>
        <el-table-column label="推荐原因" min-width="270">
          <template #default="{ row }">
            <span class="reason-text">{{ row.dispatchReason || '-' }}</span>
            <small v-if="row.responseReason">响应：{{ row.responseReason }}</small>
          </template>
        </el-table-column>
        <el-table-column label="操作" width="260" fixed="right">
          <template #default="{ row }">
            <el-button size="mini" @click.stop="openDetail(row)">详情</el-button>
            <el-button size="mini" type="success" @click.stop="retryTask(row)">重派</el-button>
            <el-button size="mini" :disabled="row.dispatchStatus !== 'PENDING'" @click.stop="cancelDispatch(row)">取消候选</el-button>
          </template>
        </el-table-column>
      </el-table>
    </section>

    <el-drawer
      v-model="detailVisible"
      size="62%"
      append-to-body
      custom-class="dispatch-drawer"
      :with-header="false"
    >
      <div class="dispatch-detail" v-if="detail">
        <header class="drawer-head">
          <div>
            <p class="eyebrow">TASK DISPATCH DETAIL</p>
            <h2>{{ taskTitle }}</h2>
            <p>{{ taskProgressText }}</p>
          </div>
          <button class="secondary-btn" @click="detailVisible = false">关闭</button>
        </header>

        <section class="detail-grid">
          <div><span>任务ID</span><strong>{{ detail.task && detail.task.id }}</strong></div>
          <div><span>服务类型</span><strong>{{ serviceTypeLabel(detail.task && detail.task.serviceType) }}</strong></div>
          <div><span>期望机器人</span><strong>{{ (detail.task && detail.task.expectedRobotType) || '-' }}</strong></div>
          <div><span>派单状态</span><strong>{{ taskProgressText }}</strong></div>
          <div><span>并发候选</span><strong>{{ detailSettings.dispatchParallelLimit || settings.dispatchParallelLimit || 3 }} 台</strong></div>
          <div><span>邀请超时</span><strong>{{ detailSettings.dispatchInviteTimeoutSeconds || settings.dispatchInviteTimeoutSeconds || 300 }} 秒</strong></div>
        </section>

        <section class="diagnosis-panel" :class="{ blocked: blockers.length }">
          <div>
            <span>{{ blockers.length ? '派单阻断原因' : '派单条件检查通过' }}</span>
            <strong>{{ blockers.length ? `${blockers.length} 项需要处理` : '可以继续自动派单' }}</strong>
          </div>
          <div class="diagnosis-list">
            <p v-for="item in blockers" :key="`block-${item}`">{{ item }}</p>
            <p v-for="item in passed" :key="`pass-${item}`">{{ item }}</p>
          </div>
        </section>

        <section class="voucher-audit-panel">
          <div>
            <p class="eyebrow">DERAS AUDIT CREDENTIALS</p>
            <h3>调度凭证与PoT审计</h3>
            <span>当前阶段生成离线审计凭证，不做上链和真实币结算。</span>
          </div>
          <div class="voucher-audit-grid">
            <span>
              <em>调度凭证</em>
              <b>{{ latestVoucherNo }}</b>
            </span>
            <span>
              <em>凭证摘要</em>
              <b>{{ shortDigest(latestVoucherDigest) }}</b>
            </span>
            <span>
              <em>凭证事件</em>
              <b>{{ voucherAuditCount }} 条</b>
            </span>
            <span>
              <em>最后状态</em>
              <b>{{ taskProgressText }}</b>
            </span>
          </div>
        </section>

        <section class="ops-panel">
          <button class="primary-btn" @click="retryCurrentTask">重新派单</button>
          <button class="secondary-btn" @click="openGrabCurrentTask">开放抢单</button>
          <button class="secondary-btn" @click="manualDispatchCurrentTask">手动指定机器人</button>
          <button class="danger-btn" @click="runLockTest">锁单压测</button>
        </section>

        <section class="table-card inner">
          <div class="section-title">
            <div>
              <p class="eyebrow">AVAILABLE ROBOT POOL</p>
              <h3>候选池与排除原因</h3>
            </div>
            <span>{{ poolSummary }}</span>
          </div>
          <el-table :data="detail.candidatePool || []">
            <el-table-column label="机器人" min-width="190">
              <template #default="{ row }">
                <strong>{{ row.robotName || `机器人 #${row.robotId || '-'}` }}</strong>
                <small>{{ row.robotCategory || '-' }} · {{ row.ownerName || row.ownerUserId || '-' }}</small>
              </template>
            </el-table-column>
            <el-table-column label="派单能力" width="150">
              <template #default="{ row }">
                <el-tag :type="row.available ? 'success' : 'warning'">{{ row.available ? '可派单' : '不可派单' }}</el-tag>
                <small>{{ row.statusText || '-' }}</small>
              </template>
            </el-table-column>
            <el-table-column label="在线/工作" width="145">
              <template #default="{ row }">
                <el-tag :type="row.sdkAgentOnline ? 'success' : 'danger'">{{ row.sdkAgentOnline ? '在线' : '离线' }}</el-tag>
                <small>{{ row.workStatus === 'BUSY' ? '任务中' : '空闲' }}</small>
              </template>
            </el-table-column>
            <el-table-column label="评分/信誉" width="150">
              <template #default="{ row }">
                <span>{{ formatScore(row.dispatchScore) }}</span>
                <small>{{ recommendationLabel(row.recommendationLevel) }} · 信誉 {{ row.ownerReputationScore || '-' }}</small>
              </template>
            </el-table-column>
            <el-table-column label="评分解释" min-width="280">
              <template #default="{ row }">
                <div class="score-breakdown">
                  <span v-for="item in row.scoreDetails || []" :key="`${row.robotId}-${item.key}`">
                    {{ item.label }} +{{ formatScore(item.contribution) }}
                  </span>
                </div>
                <small>{{ row.scoreSummary || '-' }}</small>
              </template>
            </el-table-column>
            <el-table-column label="通过项 / 排除原因" min-width="320">
              <template #default="{ row }">
                <div class="pool-reasons">
                  <span v-for="item in row.passed || []" :key="`p-${row.robotId}-${item}`" class="passed">{{ item }}</span>
                  <span v-for="item in row.blockers || []" :key="`b-${row.robotId}-${item}`" class="blocked">{{ item }}</span>
                </div>
              </template>
            </el-table-column>
          </el-table>
        </section>

        <section class="table-card inner">
          <div class="section-title">
            <div>
              <p class="eyebrow">CANDIDATE QUEUE</p>
              <h3>候选队列与响应记录</h3>
            </div>
            <span>{{ candidateSummary }}</span>
          </div>
          <el-table :data="detail.dispatches || []">
            <el-table-column label="候选" width="90">
              <template #default="{ row }">#{{ row.candidateRank || '-' }}</template>
            </el-table-column>
            <el-table-column label="机器人" min-width="180">
              <template #default="{ row }">
                <strong>{{ row.robotName || row.robotId || '-' }}</strong>
                <small>{{ row.robotCategory || '-' }} · {{ row.ownerName || '-' }}</small>
              </template>
            </el-table-column>
            <el-table-column label="状态" width="130">
              <template #default="{ row }">
                <el-tag :type="dispatchTag(row.dispatchStatus)">{{ dispatchStatusLabel(row.dispatchStatus) }}</el-tag>
              </template>
            </el-table-column>
            <el-table-column label="评分" width="100">
              <template #default="{ row }">{{ formatScore(row.dispatchScore) }}</template>
            </el-table-column>
            <el-table-column label="调度凭证" min-width="210">
              <template #default="{ row }">
                <strong>{{ row.schedulingVoucherNo || voucherNo(row) }}</strong>
                <small>{{ shortDigest(row.schedulingVoucherDigest) }}</small>
              </template>
            </el-table-column>
            <el-table-column prop="dispatchReason" label="推荐原因" min-width="260"></el-table-column>
            <el-table-column prop="responseReason" label="响应说明" min-width="180"></el-table-column>
          </el-table>
        </section>

        <section class="table-card inner">
          <div class="section-title">
            <div>
              <p class="eyebrow">DISPATCH EVENT LOG</p>
              <h3>派单事件流水</h3>
            </div>
            <span>创建、拒绝、超时、接受、取消都会沉淀到这里</span>
          </div>
          <div class="dispatch-log-list">
            <article v-for="log in detail.logs || []" :key="log.id" class="dispatch-log-item">
              <i></i>
              <div>
                <span>{{ log.createTime || '-' }} · {{ log.eventType }}</span>
                <strong>{{ log.eventTitle || '派单事件' }}</strong>
                <p>{{ log.eventContent || '-' }}</p>
              </div>
            </article>
            <p v-if="!(detail.logs || []).length" class="empty-note">暂无派单流水。</p>
          </div>
        </section>
      </div>
    </el-drawer>
  </div>
</template>

<script>
import {
  cancelAdminDispatch,
  getAdminDispatchList,
  getAdminDispatchTasks,
  getAdminDispatchTaskDetail,
  saveDerasSettings,
  manualAdminDispatch,
  openGrabAdminDispatch,
  retryAdminDispatch,
  testAdminDispatchLock
} from '../api/market'

export default {
  name: 'AdminDispatch',
  data() {
    return {
      loading: false,
      taskLoading: false,
      detailLoading: false,
      detailVisible: false,
      dispatchTasks: [],
      dispatches: [],
      summary: {},
      taskSummary: {},
      effectiveness: {},
      settings: {},
      settingForm: {
        dispatchParallelLimit: 3,
        dispatchInviteTimeoutSeconds: 300,
        dispatchMode: 'PARALLEL',
        dispatchAutoOpenGrabEnabled: 1,
        dispatchMaxAutoRounds: 3,
        dispatchDistanceWeight: 30,
        dispatchHeartbeatWeight: 20,
        dispatchRobotScoreWeight: 25,
        dispatchReputationWeight: 15,
        dispatchExperienceWeight: 10,
        homeServiceHourlyRate: 1.5,
        shuttleDefaultSubsidy: 36,
        shuttleDistanceRate: 0.8,
        shuttleHourlyRate: 1.2,
        shuttleMinimumSubsidy: 2,
        noCandidateSubsidyStep: 0.5,
        maxAutoSubsidyMultiplier: 1.5
      },
      weightFields: [
        { key: 'dispatchDistanceWeight', label: '距离权重' },
        { key: 'dispatchHeartbeatWeight', label: '心跳权重' },
        { key: 'dispatchRobotScoreWeight', label: '机器人评分' },
        { key: 'dispatchReputationWeight', label: '主人信誉' },
        { key: 'dispatchExperienceWeight', label: '同类经验' }
      ],
      pricingFields: [
        { key: 'homeServiceHourlyRate', label: '到家服务 / 小时', min: 0.1, max: 100, step: 0.1 },
        { key: 'shuttleDefaultSubsidy', label: '接驳默认补助', min: 0.1, max: 1000, step: 1 },
        { key: 'shuttleDistanceRate', label: '接驳 / 公里', min: 0.01, max: 100, step: 0.1 },
        { key: 'shuttleHourlyRate', label: '接驳 / 小时', min: 0.1, max: 100, step: 0.1 },
        { key: 'shuttleMinimumSubsidy', label: '接驳最低补助', min: 0.1, max: 1000, step: 0.5 },
        { key: 'noCandidateSubsidyStep', label: '无人候选加价', min: 0, max: 100, step: 0.1 },
        { key: 'maxAutoSubsidyMultiplier', label: '最高加价倍数', min: 1, max: 5, step: 0.1 }
      ],
      detail: null,
      filters: {
        taskId: undefined,
        status: ''
      },
      taskFilters: {
        keyword: '',
        dispatchStatus: ''
      }
    }
  },
  computed: {
    detailSettings() {
      return (this.detail && this.detail.settings) || {}
    },
    blockers() {
      return (this.detail && this.detail.diagnosis && this.detail.diagnosis.blockers) || []
    },
    passed() {
      return (this.detail && this.detail.diagnosis && this.detail.diagnosis.passed) || []
    },
    taskTitle() {
      const task = this.detail && this.detail.task
      return task ? (task.title || task.taskName || `任务 #${task.id}`) : '派单详情'
    },
    taskProgressText() {
      const task = this.detail && this.detail.task
      return task ? (task.dispatchStatusText || task.dispatchStatus || '等待派单') : '等待派单'
    },
    candidateSummary() {
      const diagnosis = (this.detail && this.detail.diagnosis) || {}
      return `同类机器人 ${diagnosis.sameTypeApprovedRobots || 0} 台，在线 ${diagnosis.onlineRobots || 0} 台，空闲 ${diagnosis.idleRobots || 0} 台`
    },
    poolSummary() {
      const pool = (this.detail && this.detail.candidatePool) || []
      const available = pool.filter(item => item.available).length
      return `可派 ${available} 台 / 总计 ${pool.length} 台`
    },
    openGrabAttentionCount() {
      return this.dispatchTasks.filter(row => row.dispatchStatus === 'OPEN_GRAB').length
    },
    blockedAttentionCount() {
      return this.dispatchTasks.filter(row => (row.blockers || []).length > 0).length
    },
    noCandidateAttentionCount() {
      return this.dispatchTasks.filter(row => Number(row.dispatchNoCandidateCount || 0) > 0).length
    },
    opsAttentionRows() {
      return this.dispatchTasks
        .filter(row => row.dispatchStatus === 'OPEN_GRAB' || Number(row.dispatchNoCandidateCount || 0) > 0 || (row.blockers || []).length > 0)
        .slice(0, 6)
    },
    latestVoucher() {
      const dispatches = (this.detail && this.detail.dispatches) || []
      if (!dispatches.length) return null
      return dispatches.find(item => item.dispatchStatus === 'ACCEPTED') || dispatches[0]
    },
    latestVoucherNo() {
      return (this.latestVoucher && (this.latestVoucher.schedulingVoucherNo || this.voucherNo(this.latestVoucher))) || '暂无凭证'
    },
    latestVoucherDigest() {
      return this.latestVoucher && this.latestVoucher.schedulingVoucherDigest
    },
    voucherAuditCount() {
      const logs = (this.detail && this.detail.logs) || []
      return logs.filter(item => String(item.eventType || '').includes('SCHEDULING_VOUCHER')).length
    }
  },
  created() {
    this.fetchDispatchTasks()
    this.fetchDispatches()
  },
  methods: {
    async fetchDispatchTasks() {
      this.taskLoading = true
      try {
        const response = await getAdminDispatchTasks({
          keyword: this.taskFilters.keyword || undefined,
          dispatchStatus: this.taskFilters.dispatchStatus || undefined
        })
        if (response.data.code !== 200) {
          this.$message.error(response.data.msg || '任务派单总览加载失败')
          return
        }
        const data = response.data.data || {}
        this.dispatchTasks = data.records || []
        this.taskSummary = data.summary || {}
      } catch (error) {
        this.$message.error('任务派单总览请求失败')
      } finally {
        this.taskLoading = false
      }
    },
    async fetchDispatches() {
      this.loading = true
      try {
        const response = await getAdminDispatchList({
          status: this.filters.status || undefined,
          taskId: this.filters.taskId || undefined
        })
        if (response.data.code !== 200) {
          this.$message.error(response.data.msg || '派单队列加载失败')
          return
        }
        const data = response.data.data || {}
        this.dispatches = data.records || []
        this.summary = data.summary || {}
        this.effectiveness = data.effectiveness || {}
        this.settings = data.settings || {}
        this.syncSettingForm()
      } catch (error) {
        this.$message.error('派单队列请求失败')
      } finally {
        this.loading = false
      }
    },
    syncSettingForm() {
      this.settingForm = {
        dispatchParallelLimit: this.settings.dispatchParallelLimit || 3,
        dispatchInviteTimeoutSeconds: this.settings.dispatchInviteTimeoutSeconds || 300,
        dispatchMode: this.settings.dispatchMode || 'PARALLEL',
        dispatchAutoOpenGrabEnabled: this.settings.dispatchAutoOpenGrabEnabled === 0 ? 0 : 1,
        dispatchMaxAutoRounds: this.settings.dispatchMaxAutoRounds || 3,
        dispatchDistanceWeight: Number(this.settings.dispatchDistanceWeight || 30),
        dispatchHeartbeatWeight: Number(this.settings.dispatchHeartbeatWeight || 20),
        dispatchRobotScoreWeight: Number(this.settings.dispatchRobotScoreWeight || 25),
        dispatchReputationWeight: Number(this.settings.dispatchReputationWeight || 15),
        dispatchExperienceWeight: Number(this.settings.dispatchExperienceWeight || 10),
        homeServiceHourlyRate: Number(this.settings.homeServiceHourlyRate || 1.5),
        shuttleDefaultSubsidy: Number(this.settings.shuttleDefaultSubsidy || 36),
        shuttleDistanceRate: Number(this.settings.shuttleDistanceRate || 0.8),
        shuttleHourlyRate: Number(this.settings.shuttleHourlyRate || 1.2),
        shuttleMinimumSubsidy: Number(this.settings.shuttleMinimumSubsidy || 2),
        noCandidateSubsidyStep: Number(this.settings.noCandidateSubsidyStep || 0.5),
        maxAutoSubsidyMultiplier: Number(this.settings.maxAutoSubsidyMultiplier || 1.5)
      }
    },
    async saveDispatchSettings() {
      try {
        const response = await saveDerasSettings({
          ...this.settings,
          dispatchParallelLimit: this.settingForm.dispatchParallelLimit,
          dispatchInviteTimeoutSeconds: this.settingForm.dispatchInviteTimeoutSeconds,
          dispatchMode: this.settingForm.dispatchMode,
          dispatchAutoOpenGrabEnabled: this.settingForm.dispatchAutoOpenGrabEnabled,
          dispatchMaxAutoRounds: this.settingForm.dispatchMaxAutoRounds,
          dispatchDistanceWeight: this.settingForm.dispatchDistanceWeight,
          dispatchHeartbeatWeight: this.settingForm.dispatchHeartbeatWeight,
          dispatchRobotScoreWeight: this.settingForm.dispatchRobotScoreWeight,
          dispatchReputationWeight: this.settingForm.dispatchReputationWeight,
          dispatchExperienceWeight: this.settingForm.dispatchExperienceWeight,
          homeServiceHourlyRate: this.settingForm.homeServiceHourlyRate,
          shuttleDefaultSubsidy: this.settingForm.shuttleDefaultSubsidy,
          shuttleDistanceRate: this.settingForm.shuttleDistanceRate,
          shuttleHourlyRate: this.settingForm.shuttleHourlyRate,
          shuttleMinimumSubsidy: this.settingForm.shuttleMinimumSubsidy,
          noCandidateSubsidyStep: this.settingForm.noCandidateSubsidyStep,
          maxAutoSubsidyMultiplier: this.settingForm.maxAutoSubsidyMultiplier
        })
        if (response.data.code !== 200) {
          this.$message.error(response.data.msg || '派单规则保存失败')
          return
        }
        this.settings = response.data.data || {}
        this.syncSettingForm()
        this.$message.success('派单规则已保存')
        this.fetchDispatches()
      } catch (error) {
        this.$message.error('派单规则保存请求失败')
      }
    },
    resetFilters() {
      this.filters = { taskId: undefined, status: '' }
      this.fetchDispatches()
    },
    resetTaskFilters() {
      this.taskFilters = { keyword: '', dispatchStatus: '' }
      this.fetchDispatchTasks()
    },
    async openTaskDetail(row) {
      const taskId = row && (row.id || row.taskId)
      if (!taskId) return
      await this.openDetail({ taskId })
    },
    async openDetail(row) {
      const taskId = row && row.taskId
      if (!taskId) return
      this.detailLoading = true
      try {
        const response = await getAdminDispatchTaskDetail(taskId)
        if (response.data.code !== 200) {
          this.$message.error(response.data.msg || '派单详情加载失败')
          return
        }
        this.detail = response.data.data || {}
        this.detailVisible = true
      } catch (error) {
        this.$message.error('派单详情请求失败')
      } finally {
        this.detailLoading = false
      }
    },
    async retryTask(row) {
      await this.runTaskAction(row.taskId, retryAdminDispatch, '已重新发起自动派单')
    },
    async retryTaskById(taskId) {
      await this.runTaskAction(taskId, retryAdminDispatch, '已重新发起自动派单')
    },
    async openGrabTaskById(taskId) {
      await this.runTaskAction(taskId, openGrabAdminDispatch, '任务已开放抢单')
    },
    async retryCurrentTask() {
      const taskId = this.detail && this.detail.task && this.detail.task.id
      await this.runTaskAction(taskId, retryAdminDispatch, '已重新发起自动派单')
      if (taskId) await this.reloadDetail(taskId)
    },
    async openGrabCurrentTask() {
      const taskId = this.detail && this.detail.task && this.detail.task.id
      await this.runTaskAction(taskId, openGrabAdminDispatch, '任务已开放抢单')
      if (taskId) await this.reloadDetail(taskId)
    },
    async runTaskAction(taskId, api, successText) {
      if (!taskId) return
      try {
        const response = await api(taskId)
        if (response.data.code !== 200) {
          this.$message.warning(response.data.msg || '操作未完成')
          return
        }
        this.$message.success(successText)
        this.fetchDispatchTasks()
        this.fetchDispatches()
      } catch (error) {
        this.$message.error('操作请求失败')
      }
    },
    async reloadDetail(taskId) {
      const response = await getAdminDispatchTaskDetail(taskId)
      if (response.data.code === 200) {
        this.detail = response.data.data || {}
      }
    },
    async cancelDispatch(row) {
      try {
        await this.$confirm(`确认取消派单 #${row.id} 吗？`, '取消候选邀请', { type: 'warning' })
        const response = await cancelAdminDispatch(row.id, { reason: '管理员取消候选邀请' })
        if (response.data.code !== 200) {
          this.$message.warning(response.data.msg || '取消失败')
          return
        }
        this.$message.success('候选邀请已取消')
        this.fetchDispatches()
        this.fetchDispatchTasks()
        if (this.detail && this.detail.task) {
          await this.reloadDetail(this.detail.task.id)
        }
      } catch (error) {
        if (error !== 'cancel') this.$message.error('取消请求失败')
      }
    },
    async manualDispatchCurrentTask() {
      const taskId = this.detail && this.detail.task && this.detail.task.id
      if (!taskId) return
      try {
        const robotId = await this.$prompt('请输入机器人ID', '手动指定机器人', {
          confirmButtonText: '继续',
          cancelButtonText: '取消',
          inputPattern: /^[1-9]\d*$/,
          inputErrorMessage: '请输入有效机器人ID'
        })
        const reasonInput = await this.$prompt('请输入派单原因', '手动指定原因', {
          confirmButtonText: '确认派单',
          cancelButtonText: '取消',
          inputValue: '管理员手动指定派单'
        })
        const response = await manualAdminDispatch(taskId, {
          robotId: Number(robotId.value),
          reason: reasonInput.value || '管理员手动指定派单'
        })
        if (response.data.code !== 200) {
          this.$message.warning(response.data.msg || '手动派单失败')
          return
        }
        this.$message.success('手动派单已发出')
        this.fetchDispatches()
        this.fetchDispatchTasks()
        await this.reloadDetail(taskId)
      } catch (error) {
        if (error !== 'cancel') this.$message.error('手动派单请求失败')
      }
    },
    async runLockTest() {
      const taskId = this.detail && this.detail.task && this.detail.task.id
      if (!taskId) return
      try {
        await this.$confirm('锁单压测会模拟多个线程同时抢同一个任务，完成后会自动恢复任务为可接单状态。只建议对测试任务执行。', '派单并发锁压测', {
          confirmButtonText: '开始压测',
          cancelButtonText: '取消',
          type: 'warning'
        })
        const response = await testAdminDispatchLock(taskId, { attempts: 8 })
        if (response.data.code !== 200) {
          this.$message.warning(response.data.msg || '锁单压测失败')
          return
        }
        const data = response.data.data || {}
        this.$alert(
          `并发次数：${data.attempts}\n成功锁单：${data.successCount}\n失败拒绝：${data.failCount}\n结果：${data.passed ? '通过，只有一个线程锁单成功' : '未通过，请检查锁单SQL'}`,
          '锁单压测结果',
          { confirmButtonText: '知道了' }
        )
        this.fetchDispatches()
        this.fetchDispatchTasks()
        await this.reloadDetail(taskId)
      } catch (error) {
        if (error !== 'cancel' && error !== 'close') this.$message.error('锁单压测请求失败')
      }
    },
    serviceTypeLabel(value) {
      if (value === 'SHUTTLE') return '接驳服务'
      if (value === 'HOME_SERVICE') return '到家服务'
      return value || '未知服务'
    },
    dispatchStatusLabel(value) {
      const labels = {
        PENDING: '等待确认',
        ACCEPTED: '已接受',
        REJECTED: '已拒绝',
        EXPIRED: '已超时',
        CANCELLED: '已取消',
        OPEN_GRAB: '开放抢单',
        SEARCHING: '寻找中',
        WAITING_REVIEW: '待审核'
      }
      return labels[value] || value || '未知'
    },
    dispatchTag(value) {
      if (value === 'ACCEPTED') return 'success'
      if (value === 'PENDING') return 'warning'
      if (value === 'SEARCHING') return ''
      if (value === 'OPEN_GRAB') return 'info'
      if (value === 'REJECTED' || value === 'EXPIRED') return 'danger'
      return 'info'
    },
    statusHint(row) {
      if (!row) return '-'
      if (row.dispatchStatus === 'PENDING') return row.expiresAt ? `等待到 ${row.expiresAt}` : '等待候选确认'
      if (row.dispatchStatus === 'ACCEPTED') return row.respondedAt ? `锁定于 ${row.respondedAt}` : '已锁定任务'
      return row.respondedAt || row.responseReason || '已结束'
    },
    formatScore(value) {
      const numberValue = Number(value || 0)
      return Number.isFinite(numberValue) ? numberValue.toFixed(2) : '0.00'
    },
    voucherNo(row) {
      return row && row.taskId && row.id ? `SV-${row.taskId}-${row.id}` : 'SV-待生成'
    },
    shortDigest(value) {
      if (!value) return 'pending'
      const text = String(value)
      return text.length > 18 ? `${text.slice(0, 18)}...` : text
    },
    percent(value) {
      const numberValue = Number(value || 0)
      return `${Number.isFinite(numberValue) ? numberValue.toFixed(2) : '0.00'}%`
    },
    recommendationLabel(value) {
      const labels = {
        BEST: '最优推荐',
        GOOD: '优先推荐',
        NORMAL: '普通候选'
      }
      return labels[value] || '普通候选'
    },
    opsAttentionReason(row) {
      if (!row) return '等待派单状态同步'
      if (row.dispatchStatus === 'OPEN_GRAB') return '已开放抢单，建议管理员关注补助、地点或人工指定机器人。'
      if (Number(row.dispatchNoCandidateCount || 0) > 0) return `已连续 ${row.dispatchNoCandidateCount || 0} 次未找到候选。`
      const blocker = (row.blockers || [])[0]
      return blocker || row.dispatchStatusText || '需要管理员关注。'
    }
  }
}
</script>

<style scoped>
.admin-manage-page {
  min-height: 100vh;
  padding: 28px;
  color: #f6fbff;
}

.page-header,
.metric-card,
.filter-card,
.table-card,
.config-strip,
.diagnosis-panel {
  border: 1px solid rgba(143, 241, 199, 0.14);
  border-radius: 20px;
  background: rgba(255, 255, 255, 0.06);
}

.page-header {
  display: flex;
  justify-content: space-between;
  gap: 20px;
  padding: 24px;
  margin-bottom: 18px;
}

.eyebrow {
  margin: 0 0 8px;
  color: #8ff1c7;
  font-size: 12px;
  letter-spacing: 0.18em;
}

.page-header h1 {
  margin: 0;
  font-size: 30px;
}

.page-header p,
.metric-card p,
small {
  display: block;
  margin-top: 7px;
  color: rgba(246, 251, 255, 0.58);
}

.metric-grid {
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  gap: 14px;
  margin-bottom: 18px;
}

.effectiveness-grid {
  margin-top: -4px;
}

.metric-card {
  padding: 18px;
}

.metric-card span,
.config-strip span {
  color: rgba(143, 241, 199, 0.84);
}

.metric-card strong {
  display: block;
  margin-top: 10px;
  font-size: 30px;
}

.metric-card.warning strong {
  color: #ffd08a;
}

.metric-card.danger strong {
  color: #ff8d8d;
}

.config-strip {
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  gap: 12px;
  padding: 16px;
  margin-bottom: 18px;
}

.config-strip div {
  padding: 12px 14px;
  border-radius: 14px;
  background: rgba(5, 13, 24, 0.42);
}

.config-strip strong {
  display: block;
  margin-top: 6px;
}

.config-strip .el-input-number {
  width: 100%;
  margin-top: 8px;
}

.config-strip .el-select {
  width: 100%;
  margin-top: 8px;
}

.config-strip .el-switch {
  display: flex;
  margin-top: 8px;
}

.weight-panel {
  padding: 16px;
  margin-bottom: 18px;
  border: 1px solid rgba(143, 241, 199, 0.14);
  border-radius: 20px;
  background: rgba(255, 255, 255, 0.06);
}

.ops-alert-card {
  padding: 16px;
  margin-bottom: 18px;
  border: 1px solid rgba(255, 208, 138, 0.22);
  border-radius: 20px;
  background:
    linear-gradient(135deg, rgba(255, 208, 138, 0.08), rgba(143, 241, 199, 0.05)),
    rgba(255, 255, 255, 0.05);
}

.ops-alert-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;
  margin-bottom: 12px;
}

.ops-alert-grid article {
  padding: 14px;
  border-radius: 16px;
  background: rgba(5, 13, 24, 0.42);
  border: 1px solid rgba(143, 241, 199, 0.12);
}

.ops-alert-grid span,
.ops-alert-grid p {
  display: block;
  color: rgba(246, 251, 255, 0.58);
}

.ops-alert-grid strong {
  display: block;
  margin: 8px 0;
  font-size: 26px;
}

.ops-alert-grid .warning strong {
  color: #ffd08a;
}

.ops-alert-grid .danger strong {
  color: #ff8d8d;
}

.ops-attention-list {
  display: grid;
  gap: 8px;
}

.ops-attention-list button {
  display: grid;
  grid-template-columns: minmax(160px, 0.45fr) minmax(0, 1fr);
  gap: 12px;
  align-items: center;
  padding: 12px 14px;
  border: 1px solid rgba(255, 208, 138, 0.16);
  border-radius: 14px;
  color: #f6fbff;
  text-align: left;
  background: rgba(5, 13, 24, 0.42);
  cursor: pointer;
}

.ops-attention-list button:hover {
  border-color: rgba(255, 208, 138, 0.36);
  background: rgba(255, 208, 138, 0.08);
}

.ops-attention-list span {
  color: rgba(246, 251, 255, 0.62);
}

.section-title.compact {
  margin-bottom: 12px;
}

.weight-grid {
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  gap: 12px;
}

.weight-grid label {
  padding: 12px 14px;
  border-radius: 14px;
  background: rgba(5, 13, 24, 0.42);
}

.weight-grid label span {
  display: block;
  margin-bottom: 8px;
  color: rgba(143, 241, 199, 0.84);
  font-size: 13px;
}

.weight-grid .el-input-number {
  width: 100%;
}

.mini-save-btn {
  width: 100%;
  height: 34px;
  margin-top: 8px;
  border: none;
  border-radius: 999px;
  color: #03101d;
  font-weight: 800;
  cursor: pointer;
  background: linear-gradient(135deg, #8ff1c7, #ffd08a);
}

.filter-card {
  display: grid;
  grid-template-columns: 180px 180px 110px 110px;
  gap: 12px;
  padding: 16px;
  margin-bottom: 18px;
}

.filter-card .el-input {
  width: 100%;
}

.dispatch-filter-card {
  margin-top: 18px;
}

.table-card {
  padding: 16px;
}

.task-overview-card {
  margin-bottom: 18px;
}

.table-card.inner {
  margin-top: 18px;
}

.primary-btn,
.secondary-btn {
  height: 40px;
  min-width: 108px;
  border-radius: 999px;
  font-weight: 800;
  cursor: pointer;
}

.primary-btn {
  border: none;
  color: #03101d;
  background: linear-gradient(135deg, #8ff1c7, #ffd08a);
}

.secondary-btn {
  border: 1px solid rgba(255, 255, 255, 0.14);
  color: rgba(246, 251, 255, 0.84);
  background: rgba(255, 255, 255, 0.06);
}

.danger-btn {
  height: 40px;
  min-width: 108px;
  border: 1px solid rgba(255, 105, 105, 0.42);
  border-radius: 999px;
  color: #fff;
  font-weight: 800;
  cursor: pointer;
  background: rgba(240, 68, 97, 0.78);
}

.reason-text {
  display: -webkit-box;
  overflow: hidden;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
}

.pool-reasons {
  display: flex;
  flex-wrap: wrap;
  gap: 7px;
}

.score-breakdown {
  display: flex;
  flex-wrap: wrap;
  gap: 7px;
}

.score-breakdown span {
  padding: 5px 8px;
  border-radius: 999px;
  color: #dfffee;
  font-size: 12px;
  background: rgba(143, 241, 199, 0.12);
}

.pool-reasons span {
  display: inline-flex;
  margin-top: 0;
  padding: 5px 8px;
  border-radius: 999px;
  font-size: 12px;
  line-height: 1.35;
}

.pool-reasons .passed {
  color: #bfffe3;
  background: rgba(143, 241, 199, 0.12);
}

.pool-reasons .blocked {
  color: #ffd9ad;
  background: rgba(255, 179, 107, 0.13);
}

.compact-reasons {
  gap: 5px;
}

.compact-reasons span {
  max-width: 100%;
  white-space: normal;
}

.drawer-head {
  display: flex;
  justify-content: space-between;
  gap: 18px;
  padding: 26px;
  border-bottom: 1px solid rgba(143, 241, 199, 0.14);
}

.drawer-head h2 {
  margin: 0;
}

.drawer-head p {
  margin: 8px 0 0;
  color: rgba(246, 251, 255, 0.6);
}

.dispatch-detail {
  color: #f6fbff;
}

.detail-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;
  padding: 18px 26px 0;
}

.detail-grid div {
  padding: 14px;
  border-radius: 14px;
  background: rgba(255, 255, 255, 0.06);
}

.detail-grid span,
.diagnosis-panel span {
  display: block;
  color: rgba(246, 251, 255, 0.55);
  font-size: 12px;
}

.detail-grid strong,
.diagnosis-panel strong {
  display: block;
  margin-top: 7px;
  color: #fff;
}

.diagnosis-panel {
  display: grid;
  grid-template-columns: 220px 1fr;
  gap: 16px;
  margin: 18px 26px 0;
  padding: 16px;
}

.diagnosis-panel.blocked {
  border-color: rgba(255, 208, 138, 0.36);
  background: rgba(255, 208, 138, 0.08);
}

.diagnosis-list {
  display: grid;
  gap: 8px;
}

.diagnosis-list p {
  margin: 0;
  padding: 9px 12px;
  border-radius: 12px;
  color: rgba(246, 251, 255, 0.82);
  background: rgba(5, 13, 24, 0.42);
}

.voucher-audit-panel {
  display: grid;
  grid-template-columns: 260px minmax(0, 1fr);
  gap: 16px;
  margin: 18px 26px 0;
  padding: 16px;
  border-radius: 18px;
  background:
    linear-gradient(135deg, rgba(119, 184, 255, 0.12), rgba(255, 208, 138, 0.08)),
    rgba(8, 18, 30, 0.74);
  border: 1px solid rgba(119, 184, 255, 0.18);
}

.voucher-audit-panel h3 {
  margin: 0 0 8px;
  color: #fff;
}

.voucher-audit-panel span {
  color: rgba(246, 251, 255, 0.62);
}

.voucher-audit-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 10px;
}

.voucher-audit-grid span {
  padding: 12px;
  border-radius: 14px;
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.08);
}

.voucher-audit-grid em,
.voucher-audit-grid b {
  display: block;
  font-style: normal;
}

.voucher-audit-grid em {
  margin-bottom: 7px;
  color: rgba(255, 208, 138, 0.82);
  font-size: 12px;
}

.voucher-audit-grid b {
  color: #fff;
  word-break: break-word;
}

.ops-panel {
  display: flex;
  gap: 12px;
  padding: 18px 26px 0;
}

.section-title {
  display: flex;
  justify-content: space-between;
  gap: 16px;
  align-items: flex-end;
  margin-bottom: 14px;
}

.section-title h3 {
  margin: 0;
}

.section-title span {
  color: rgba(246, 251, 255, 0.55);
}

.dispatch-log-list {
  display: grid;
  gap: 10px;
}

.dispatch-log-item {
  display: grid;
  grid-template-columns: 14px 1fr;
  gap: 12px;
  padding: 13px 14px;
  border-radius: 14px;
  border: 1px solid rgba(143, 241, 199, 0.12);
  background: rgba(5, 13, 24, 0.42);
}

.dispatch-log-item i {
  width: 10px;
  height: 10px;
  margin-top: 5px;
  border-radius: 999px;
  background: #8ff1c7;
  box-shadow: 0 0 0 6px rgba(143, 241, 199, 0.08);
}

.dispatch-log-item span {
  color: rgba(246, 251, 255, 0.48);
  font-size: 12px;
}

.dispatch-log-item strong {
  display: block;
  margin-top: 5px;
}

.dispatch-log-item p,
.empty-note {
  margin: 6px 0 0;
  color: rgba(246, 251, 255, 0.66);
  line-height: 1.65;
}

@media (max-width: 1100px) {
  .metric-grid,
  .config-strip,
  .ops-alert-grid,
  .weight-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .detail-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 760px) {
  .admin-manage-page {
    padding: 18px;
  }

    .page-header,
    .metric-grid,
    .config-strip,
    .ops-alert-grid,
    .weight-grid,
    .filter-card,
    .detail-grid,
  .diagnosis-panel {
    grid-template-columns: 1fr;
    flex-direction: column;
  }

  .ops-attention-list button {
    grid-template-columns: 1fr;
  }
}
</style>
